// flow/hooks/useFlowState.ts
import { useState, useEffect, useCallback, useMemo } from 'react'
import { backgroundPresets } from '@/utils/backgroundPresets'
import type { FlowState } from '../types'

export function useFlowState(projectName?: string) {
    const [theme, setTheme] = useState<'light' | 'dark'>('light')
    const [backgroundPreset, setBackgroundPreset] = useState<string>(
        () => localStorage.getItem('flowBackground') || 'default'
    )
    const [showGlitch, setShowGlitch] = useState(true)
    const [versionGlitch, setVersionGlitch] = useState(true)
    const [showMap, setShowMap] = useState(true)
    const [panOnScroll, setPanOnScroll] = useState(false)
    const [debug, setDebug] = useState(false)

    // Dialog states
    const [showNodeIndex, setShowNodeIndex] = useState(false)
    const [showFileDialog, setShowFileDialog] = useState(false)
    const [showBlueprints, setShowBlueprints] = useState(false)
    const [showProjectSettings, setShowProjectSettings] = useState(false)
    const [showHelpGuide, setShowHelpGuide] = useState(false)

    // Project info
    const [projectInfo, setProjectInfo] = useState({
        projectName: projectName || '',
        projectDescription: '',
    })

    // Flow bounds
    const [flowBounds, setFlowBounds] = useState<DOMRect | null>(null)

    // Creativeness
    const [creativeness, setCreativeness] = useState<number>(() => {
        try {
            const settings = (window as any).syncedProjectStore?.project?.settings || null
            if (Array.isArray(settings) && settings[0] && typeof settings[0].creativeness === 'number') {
                return settings[0].creativeness
            } else if (typeof settings?.creativeness === 'number') {
                return settings.creativeness
            }
        } catch (e) { }
        return 50 // Default if not found
    })

    // Theme effects
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null
        const initialTheme = savedTheme || 'light'
        setTheme(initialTheme)
        document.documentElement.setAttribute('data-theme', initialTheme)
    }, [])

    // Background preset effects
    useEffect(() => {
        const currentSaved = localStorage.getItem('flowBackground')
        if (currentSaved !== backgroundPreset) {
            localStorage.setItem('flowBackground', backgroundPreset)
            console.log('Background preference saved:', backgroundPreset)
        }
    }, [backgroundPreset])

    // Glitch effects
    useEffect(() => {
        if (showGlitch) {
            const timer = setTimeout(() => setShowGlitch(false), 1500)
            return () => clearTimeout(timer)
        }
    }, [showGlitch])

    useEffect(() => {
        if (versionGlitch) {
            const timer = setTimeout(() => setVersionGlitch(false), 1000)
            return () => clearTimeout(timer)
        }
    }, [versionGlitch])
    // Phase change effect
    useEffect(() => {
        const handlePhaseChange = () => {
            setTimeout(() => setShowGlitch(false), 1500)
        }
        document.addEventListener('phase-change', handlePhaseChange as EventListener)
        return () => document.removeEventListener('phase-change', handlePhaseChange as EventListener)
    }, [])

    // Creativeness sync effect
    useEffect(() => {
        const interval = setInterval(() => {
            try {
                const settings = (window as any).syncedProjectStore?.project?.settings || null
                let backendCreativeness = 50
                if (Array.isArray(settings) && settings[0] && typeof settings[0].creativeness === 'number') {
                    backendCreativeness = settings[0].creativeness
                } else if (typeof settings?.creativeness === 'number') {
                    backendCreativeness = settings.creativeness
                }
                setCreativeness((prev) => prev !== backendCreativeness ? backendCreativeness : prev)
            } catch (e) { }
        }, 1000)
        return () => clearInterval(interval)
    }, [])

    // Theme toggle handler
    const toggleTheme = useCallback(() => {
        const newTheme = theme === 'light' ? 'dark' : 'light'
        setTheme(newTheme)
        localStorage.setItem('theme', newTheme)
        document.documentElement.setAttribute('data-theme', newTheme)
    }, [theme])

    // Current background preset
    const currentBackgroundPreset = useMemo(() => {
        return backgroundPresets.find((preset) => preset.id === backgroundPreset) || backgroundPresets[0]
    }, [backgroundPreset])

    const state: FlowState = {
        theme,
        backgroundPreset,
        showGlitch,
        versionGlitch,
        showMap,
        panOnScroll,
        debug,
        showNodeIndex,
        showFileDialog,
        showBlueprints,
        showProjectSettings,
        showHelpGuide,
        projectInfo,
        flowBounds,
        creativeness,
    }

    const handlers = {
        toggleTheme,
        setBackgroundPreset,
        setShowNodeIndex,
        setShowFileDialog,
        setShowBlueprints,
        setShowProjectSettings,
        setShowHelpGuide,
        setProjectInfo,
        setFlowBounds,
        setCreativeness,
        setShowMap,
        setPanOnScroll,
        setDebug,
        setShowGlitch,
        setVersionGlitch,
    }

    return {
        state,
        handlers,
        currentBackgroundPreset,
    }
}
